import { useState, useEffect, useCallback, useRef } from 'react';
import {
  collection,
  onSnapshot,
  doc,
  setDoc,
  deleteDoc,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../contexts/firebase';
import { usePlanning } from '../../contexts/PlanningContext';
import { MagicGridRow } from './types';

/**
 * Generate a simple unique ID for rows stored in _Data JSON.
 */
function generateRowId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Hook that connects MagicGrid to Firestore.
 * Resolves {companyId} in the path from PlanningContext.
 *
 * Supports two Firestore patterns:
 *
 * 1. Collection mode (odd segment count, e.g. "companies/{id}/Hats"):
 *    Each row is a separate Firestore document in the collection.
 *
 * 2. Document mode (even segment count, e.g. "companies/{id}/Hats/content"):
 *    All rows are stored as a JSON string in a single document's `_Data` field.
 *    This matches the original Vue MagicGrid pattern from FedermanBenjamin.
 */
export function useMagicGridData(
  firestorePath?: string,
  staticItems?: MagicGridRow[]
) {
  const { currentCompany } = usePlanning();
  const [rows, setRows] = useState<MagicGridRow[]>([]);
  const [loading, setLoading] = useState(true);

  // Keep a ref to the latest _Data rows for document-mode mutations
  const docDataRef = useRef<MagicGridRow[]>([]);

  // Resolve path placeholders
  const resolvedPath = firestorePath
    ? firestorePath.replace('{companyId}', currentCompany?.id || '')
    : null;

  // Even segments = document path (Vue _Data pattern)
  // Odd segments = collection path (individual docs)
  const segmentCount = resolvedPath
    ? resolvedPath.split('/').filter(Boolean).length
    : 0;
  const isDocMode = segmentCount > 0 && segmentCount % 2 === 0;
  const isCollectionMode = segmentCount > 0 && segmentCount % 2 === 1;

  // Subscribe to Firestore
  useEffect(() => {
    if (staticItems) {
      setRows(staticItems);
      setLoading(false);
      return;
    }

    if (!resolvedPath || !currentCompany?.id) {
      setRows([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    if (isDocMode) {
      // Document mode: listen to a single doc, parse _Data JSON
      const docRef = doc(db, resolvedPath);
      const unsub = onSnapshot(
        docRef,
        (snapshot) => {
          if (!snapshot.exists()) {
            // Doc doesn't exist yet — start with empty grid
            docDataRef.current = [];
            setRows([]);
            setLoading(false);
            return;
          }

          const data = snapshot.data();
          let parsed: MagicGridRow[] = [];

          if (data?._Data) {
            try {
              const raw = JSON.parse(data._Data);
              if (Array.isArray(raw)) {
                parsed = raw.map((item: any, idx: number) => ({
                  ...item,
                  id: item.id || `row-${idx}`,
                }));
              }
            } catch (err) {
              console.error('MagicGrid: Failed to parse _Data JSON:', err);
            }
          }

          docDataRef.current = parsed;
          setRows(parsed);
          setLoading(false);
        },
        (err) => {
          console.error('MagicGrid doc snapshot error:', err);
          setLoading(false);
        }
      );

      return () => unsub();
    }

    if (isCollectionMode) {
      // Collection mode: each row is a separate doc
      const colRef = collection(db, resolvedPath);
      const unsub = onSnapshot(
        colRef,
        (snapshot) => {
          const data: MagicGridRow[] = snapshot.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }));
          setRows(data);
          setLoading(false);
        },
        (err) => {
          console.error('MagicGrid collection snapshot error:', err);
          setLoading(false);
        }
      );

      return () => unsub();
    }

    // Should not reach here
    setRows([]);
    setLoading(false);
  }, [resolvedPath, currentCompany?.id, staticItems, isDocMode, isCollectionMode]);

  // Helper: persist _Data JSON back to the document
  const persistDocData = useCallback(
    async (updatedRows: MagicGridRow[]) => {
      if (!resolvedPath || !isDocMode) return;
      const docRef = doc(db, resolvedPath);
      // Strip internal id before saving (Vue format stores plain objects)
      const toSave = updatedRows.map(({ id: _id, ...rest }) => rest);
      await setDoc(
        docRef,
        {
          _Data: JSON.stringify(toSave),
          UpdateDate: serverTimestamp(),
        },
        { merge: true }
      );
    },
    [resolvedPath, isDocMode]
  );

  const addRow = useCallback(
    async (row: Record<string, any>) => {
      if (!resolvedPath) return;

      if (isDocMode) {
        const newRow = { ...row, id: generateRowId() };
        const updated = [...docDataRef.current, newRow];
        docDataRef.current = updated;
        await persistDocData(updated);
        return;
      }

      const colRef = collection(db, resolvedPath);
      await addDoc(colRef, {
        ...row,
        createdDate: serverTimestamp(),
        updateDate: serverTimestamp(),
      });
    },
    [resolvedPath, isDocMode, persistDocData]
  );

  const updateRow = useCallback(
    async (id: string, updates: Record<string, any>) => {
      if (!resolvedPath) return;

      if (isDocMode) {
        const updated = docDataRef.current.map((r) =>
          r.id === id ? { ...r, ...updates } : r
        );
        docDataRef.current = updated;
        await persistDocData(updated);
        return;
      }

      const docRef = doc(db, resolvedPath, id);
      await setDoc(docRef, { ...updates, updateDate: serverTimestamp() }, { merge: true });
    },
    [resolvedPath, isDocMode, persistDocData]
  );

  const deleteRow = useCallback(
    async (id: string) => {
      if (!resolvedPath) return;

      if (isDocMode) {
        const updated = docDataRef.current.filter((r) => r.id !== id);
        docDataRef.current = updated;
        await persistDocData(updated);
        return;
      }

      const docRef = doc(db, resolvedPath, id);
      await deleteDoc(docRef);
    },
    [resolvedPath, isDocMode, persistDocData]
  );

  const addRows = useCallback(
    async (newRows: Record<string, any>[]) => {
      if (!resolvedPath) return;

      if (isDocMode) {
        const withIds = newRows.map((r) => ({ ...r, id: generateRowId() }));
        const updated = [...docDataRef.current, ...withIds];
        docDataRef.current = updated;
        await persistDocData(updated);
        return;
      }

      const colRef = collection(db, resolvedPath);
      for (const row of newRows) {
        await addDoc(colRef, {
          ...row,
          createdDate: serverTimestamp(),
          updateDate: serverTimestamp(),
        });
      }
    },
    [resolvedPath, isDocMode, persistDocData]
  );

  return { rows, loading, addRow, updateRow, deleteRow, addRows };
}
